// Dependencies
import { FC, useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import {
  Description,
  ExpandCircleDown,
  FormatColorText,
  ShortText,
  TableRows,
  ViewModule,
  SpaceBar,
  FolderSpecial,
  ViewColumn,
  DashboardCustomize,
  Analytics,
} from '@mui/icons-material';

// Components
import { Dialog } from '../../Dialog';

// Styles
import styles from './styles';

import { Chart10Icon, Chart12Icon, Chart5Icon } from '../../../assets/icons';
import { AppContext } from '../../../context';
import { ROLE } from '../../../resource';

// Constants
export const contentItems = [
  {
    icon: <ExpandCircleDown />,
    type: 'collapse',
    label: 'Collapse',
    content: [],
  },
  {
    icon: <ShortText />,
    type: 'text-row',
    label: 'Text Row',
    content: '',
  },
  {
    icon: <Chart5Icon />,
    type: 'bar-chart',
    label: 'Bar Chart',
    content: { categories: [], chartData: [] },
  },
  {
    icon: <Chart12Icon />,
    type: 'pie-chart',
    label: 'Pie Chart',
    content: { showBlocks: false, chartData: [] },
  },
  {
    icon: <ViewModule />,
    type: 'block',
    label: 'Blocks',
    content: { title: '', subtitle: '', isDynamic: false, data: [] },
  },
  {
    icon: <Chart10Icon />,
    type: 'history',
    label: 'History Table',
    content: {
      text: '',
      title: '',
      isPercent: false,
      ranges: ['Gesamt', '1 Jahr', '1 Monat', '6 Monate', 'YTD'],
    },
  },
  {
    icon: <Description />,
    type: 'document-list',
    label: 'Documents',
    content: [],
  },
  {
    icon: <TableRows />,
    type: 'table',
    label: 'Table',
    content: { themeIndex: 0, header: [''], body: [['']], columns: [{ type: 'text' }] },
  },
  {
    icon: <FormatColorText />,
    type: 'rich-text',
    label: 'Rich Text',
    content: '',
  },
  {
    icon: <SpaceBar />,
    type: 'spacer',
    label: 'Spacer',
    content: 24,
  },
  {
    icon: <Analytics />,
    type: 'risk-indicator',
    label: 'Risk Indicator',
    content: {
      riskCount: 5,
      colors: ['#6EA8FE', '#9EE493'],
      labelLow: 'Low',
      labelHigh: 'High',
      descriptionLow: '',
      descriptionHigh: '',
    },
  },
  {
    icon: <FolderSpecial />,
    type: 'custom-portfolio',
    label: 'Custom Portfolio',
    content: {
      portfolioUploadId: '',
      isins: [],
    },
  },
  {
    icon: <ViewColumn />,
    type: 'row',
    label: '2-column layout',
    content: {
      gap: 24,
      columns: [
        { width: 50, widget: null },
        { width: 50, widget: null },
      ],
    },
  },
];

const WRAP_EXCLUDE_TYPES = ['row', 'custom-portfolio'];

function typeLabel(type: string): string {
  return contentItems.find((i) => i.type === type)?.label || type;
}

function widgetSummary(w: any): string {
  const base = typeLabel(w?.type);
  const title =
    (typeof w?.content?.title === 'string' && w.content.title.trim()) ||
    (typeof w?.content === 'string' && w.content.trim().slice(0, 48)) ||
    (typeof w?.content?.text === 'string' && w.content.text.trim().slice(0, 48)) ||
    '';
  return title ? `${base} — ${title}` : base;
}

interface IWidgetListDialog {
  open: boolean;
  onClose: () => void;
  onSelect: (value: any) => void;
  /** Hide types from the picker (e.g. prevent nesting `row` inside `row`). */
  excludeTypes?: string[];
  /**
   * When set (e.g. adding into a 2-column layout slot), show “From existing”
   * to pick widgets already on the tab.
   */
  existingWidgets?: any[];
  /** Called with the checked widgets (order preserved from `existingWidgets`). */
  onWrapExisting?: (widgets: any[]) => void;
  /** Max checkboxes in “From existing” (default 2). Use 1 when filling a single column. */
  maxWrapSelection?: number;
}

export const WidgetListDialog: FC<IWidgetListDialog> = ({
  open,
  onClose,
  onSelect,
  excludeTypes = [],
  existingWidgets = [],
  onWrapExisting,
  maxWrapSelection = 2,
}) => {
  const { account } = useContext(AppContext);
  const [mode, setMode] = useState<'pick' | 'wrap'>('pick');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const wrappable = useMemo(
    () =>
      (existingWidgets || []).filter(
        (w) => w && w.id != null && w.type && !WRAP_EXCLUDE_TYPES.includes(w.type)
      ),
    [existingWidgets]
  );

  const canWrap = typeof onWrapExisting === 'function' && wrappable.length > 0;
  const selectionLimit = Math.max(1, maxWrapSelection);

  const canSeeType = (type: string) =>
    account?.role === ROLE.ADMIN || account?.allowedWidgets?.includes(type);

  const handleClose = () => {
    setMode('pick');
    setSelectedIds([]);
    onClose();
  };

  const handleSelect = (item: (typeof contentItems)[number]) => {
    const raw = item.content;
    const content =
      typeof raw === 'object' && raw !== null ? JSON.parse(JSON.stringify(raw)) : raw;
    onSelect({
      id: +new Date(),
      type: item.type,
      content,
    });
    handleClose();
  };

  const toggleId = (id: number) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= selectionLimit) {
        // Single-select: replace previous choice
        if (selectionLimit === 1) return [id];
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleConfirmWrap = () => {
    if (!onWrapExisting || selectedIds.length === 0) return;
    const chosen = wrappable.filter((w) => selectedIds.includes(w.id));
    onWrapExisting(chosen);
    handleClose();
  };

  const pickerItems = contentItems.filter((item) => {
    if (excludeTypes.includes(item.type)) return false;
    return canSeeType(item.type);
  });

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.form}>
        {mode === 'pick' ? (
          <Grid container columns={3} rowSpacing={20}>
            {pickerItems.map((item, index) => (
              <Grid key={index} item xs={1}>
                <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
                  <ButtonBase sx={styles.chart} onClick={() => handleSelect(item)}>
                    {item.icon}
                  </ButtonBase>
                  <Typography variant="subtitle2">{item.label}</Typography>
                </Box>
              </Grid>
            ))}

            {canWrap && (
              <Grid item xs={1}>
                <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
                  <ButtonBase
                    sx={styles.chart}
                    onClick={() => {
                      setSelectedIds([]);
                      setMode('wrap');
                    }}
                  >
                    <DashboardCustomize />
                  </ButtonBase>
                  <Typography variant="subtitle2" textAlign="center">
                    From existing
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        ) : (
          <Box sx={{ width: '100%', maxWidth: 480 }}>
            <Typography variant="h6" sx={{ mb: 8 }}>
              Use existing tab widget
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 12 }}>
              {selectionLimit === 1
                ? 'Select one widget from this tab. It will be moved into this column and removed from the top-level list.'
                : `Select up to ${selectionLimit} widgets from this tab.`}
            </Typography>

            <Box
              sx={{
                maxHeight: 320,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 8,
                mb: 16,
                backgroundColor: 'background.paper',
              }}
            >
              {wrappable.map((w) => {
                const checked = selectedIds.includes(w.id);
                const disabled = !checked && selectedIds.length >= selectionLimit && selectionLimit > 1;
                return (
                  <FormControlLabel
                    key={w.id}
                    sx={{
                      display: 'flex',
                      width: '100%',
                      m: 0,
                      mb: 4,
                      alignItems: 'flex-start',
                      opacity: disabled ? 0.5 : 1,
                    }}
                    control={
                      <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleId(w.id)}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ pt: 4 }}>
                        <Typography variant="body2">{widgetSummary(w)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {w.type}
                          {w.id != null ? ` · #${w.id}` : ''}
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
              {wrappable.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No widgets available to move.
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setMode('pick');
                  setSelectedIds([]);
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                disabled={selectedIds.length === 0}
                onClick={handleConfirmWrap}
              >
                Move into column ({selectedIds.length}/{selectionLimit})
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};
